import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import { createApiClient } from '@/shared/api/apiClient';
import type { components } from '@/shared/api/openapi';

export interface UseUserBudgetGroupMembershipOptions {
  userId: string;
  organizationId: number;
  groupIds: number[];
  enabled?: boolean;
}

type BudgetGroupMembership = components['schemas']['CheckBudgetGroupMembershipEndpoint_Response'];

export function useUserBudgetGroupMembership({ userId, organizationId, groupIds, enabled = true }: UseUserBudgetGroupMembershipOptions) {
  const { getAccessToken } = useAuth();

  return useQuery({
    queryKey: ['user-budget-group-membership', organizationId, userId, groupIds],
    queryFn: async ({ signal }) => {
      const client = createApiClient(getAccessToken, {
        cache: 'no-store',
        signal,
      });

      const { data, error } = await client.POST('/api/organizations/{organizationId}/users/{userId}/budget-groups/check', {
        params: {
          path: { organizationId, userId },
        },
        body: { groupIds },
      });

      if (error) {
        throw new Error(error.title || 'Failed to check budget group membership');
      }

      return (data ?? []) as BudgetGroupMembership[];
    },
    enabled: enabled && groupIds.length > 0,
    staleTime: 30000,
  });
}
